import React, { useEffect, useState, MouseEvent } from "react";
import { useSelector } from "react-redux";
import { get } from "lodash";

import mediator from "@tvg/mediator";
import buildRaceUrl from "@tvg/formatter/url";
import { RaceTypeCodeEnum } from "@tvg/ts-types/Race";
import HomeHeader from "@fdr/atomic-ui/_templates/HomeHeader";
import {
  DerbyEventStatus,
  IBettingInterest,
  IInterestComputedOdds
} from "@fdr/types/ts/Derby";
import { BannerContext, HeaderBanner } from "@urp/derby-banner/src/types";
import { Props as DerbyPickProps } from "@fdr/atomic-ui/_molecules/DerbyPick";
import { FooterButton } from "@fdr/atomic-ui/_organisms/HomeHeaderFooter/types";
import { RunnerChance } from "@fdr/atomic-ui/_molecules/DerbyPick/types";
import { useNavigate } from "react-router";
import HomeHeaderBannerImage from "./components/HomeHeader";
import { GtmEventProps, IProps } from "./types";
import {
  getHomepageDerbyBanner,
  getDerbyBannerImageToggle
} from "./store/selectors";

const DerbyPicksWinningProbabilities: Array<RunnerChance> = [
  "1ST",
  "2ND",
  "3RD"
];

const buildDerbyPicksDisplay = (
  bettingInterests: IBettingInterest[],
  isNumberFire = false,
  baseBet: number
): Array<DerbyPickProps> => {
  // append computed odd to every derby pick
  const bettingInterestsWithComputedOdds: IInterestComputedOdds[] =
    bettingInterests.reduce(
      (interests: IInterestComputedOdds[], interest: IBettingInterest) => {
        if (!interest.runners[0].scratched) {
          interests.push({
            ...interest,
            computedOdd: get(interest, "currentOdds.denominator")
              ? get(interest, "currentOdds.numerator", 1) /
                get(interest, "currentOdds.denominator")
              : get(interest, "currentOdds.numerator", 1) / 1
          });
        }
        return interests;
      },
      []
    );

  // sort the array for picking the right values by odds
  const sortedDerbyPicksByOdds: IInterestComputedOdds[] =
    bettingInterestsWithComputedOdds.sort((interest1, intereset2) =>
      interest1.computedOdd > intereset2.computedOdd ? 1 : -1
    );

  // Choose the best 3 ones
  // first - favorite, last two ones - consider, longshot
  //
  const builtArrayByNumberFireFlag = sortedDerbyPicksByOdds.slice(0, 3);

  // build the picks as props for the derbyPicks component
  return builtArrayByNumberFireFlag.map((pick, index) => ({
    runnerId: +pick.runners[0].runnerId,
    runnerName: pick.runners[0].horseName,
    odds: pick.currentOdds,
    // TODO: picking the first - waiting for further explanation on this
    runnerChance: DerbyPicksWinningProbabilities[index],
    raceTypeCode: RaceTypeCodeEnum.THOROUGHBRED,
    // the amount computed for 10 dollar bet
    winAmount: baseBet + 10 * pick.computedOdd,
    isNumberFire
  }));
};

const sendGTMEvent = ({ linkUrl, bannerName, linkText }: GtmEventProps) => {
  mediator.base.dispatch({
    type: "DERBY_HEADER_CLICK",
    payload: {
      linkUrl,
      bannerName,
      linkText
    }
  });
};

const HomeHeaderWrapper = ({
  title,
  subtitle,
  description,
  isNumberFire,
  trackAbbr,
  showDerbyPicks,
  eventStatus = DerbyEventStatus.POST,
  promoLink,
  promoText,
  derbyRace,
  homepageCTA,
  eventDate,
  baseBet = 10
}: IProps) => {
  const navigate = useNavigate();
  const [userSeenHeader, setUserSeenHeader] = useState(false);
  const derbyBannerImageToggle = useSelector(getDerbyBannerImageToggle);
  const homePageDerbyBannerCfg = useSelector(getHomepageDerbyBanner);

  const bannerCfg = (
    get(homePageDerbyBannerCfg, `${eventStatus}`, []) as Array<HeaderBanner>
  ).find((cfg) => cfg.context.includes(BannerContext.HOME_PAGE));

  let headerCTA = null;
  const derbyPath = buildRaceUrl(
    trackAbbr,
    get(derbyRace, "trackName", ""),
    get(derbyRace, "raceNumber", "")
  );

  const derbyPicks = buildDerbyPicksDisplay(
    get(derbyRace, "bettingInterests", []),
    isNumberFire,
    baseBet
  );
  let raceDate = eventDate;

  if (eventStatus === DerbyEventStatus.DERBY_DAY) {
    headerCTA = get(homepageCTA, "eventDay", null);
    const racePostTime = get(derbyRace, "postTime");
    raceDate = racePostTime ? new Date(racePostTime) : null;
  } else if (eventStatus === DerbyEventStatus.PRE) {
    headerCTA = get(homepageCTA, "preEvent", null);
  }

  useEffect(() => {
    if (!userSeenHeader && eventStatus !== DerbyEventStatus.POST) {
      mediator.base.dispatch({
        type: "DERBY_HEADER_VIEWED",
        payload: {
          bannerName: get(title, "label", "")
        }
      });

      setUserSeenHeader(true);
    }
  }, [eventStatus]);

  const onHeaderClick = () => {
    if (derbyPath && eventStatus === DerbyEventStatus.DERBY_DAY) {
      const titleText = get(title, "label", "");
      sendGTMEvent({
        linkUrl: derbyPath,
        bannerName: titleText,
        linkText: titleText
      });
      navigate(derbyPath);
    }
  };

  const onPromoClick = (event: MouseEvent<HTMLDivElement>) => {
    if (promoLink) {
      const titleText = get(title, "label", "");

      const promoFormattedLink = `?promo=${promoLink}#promos`;
      event.stopPropagation();
      sendGTMEvent({
        linkUrl: promoFormattedLink,
        bannerName: titleText,
        linkText: promoText
      });
      navigate(promoFormattedLink);
    }
  };

  const onFooterButtonClick = ({ url, text }: FooterButton) => {
    const titleText = get(title, "label", "");

    sendGTMEvent({
      linkUrl: url,
      bannerName: titleText,
      linkText: text
    });
  };

  const onBannerCtaClick = (label: string, url: string) => {
    const titleText = get(title, "label", "");
    const linkUrl = url.includes("#derbyRace") ? derbyPath : url;

    sendGTMEvent({
      linkUrl,
      bannerName: titleText, // TODO: confirm this value
      linkText: label
    });
    navigate(linkUrl);
  };

  return derbyBannerImageToggle && bannerCfg ? (
    <HomeHeaderBannerImage
      raceStartDate={raceDate}
      eventStatus={eventStatus}
      showDerbyPicks={showDerbyPicks && !!derbyPicks.length}
      derbyPicks={derbyPicks}
      isNumberFire={isNumberFire}
      onBannerCtaClick={onBannerCtaClick}
      baseBet={baseBet}
      bannerCfg={bannerCfg}
    />
  ) : (
    <HomeHeader
      raceStartDate={raceDate}
      eventStatus={eventStatus}
      title={title}
      subtitle={subtitle}
      description={description}
      showDerbyPicks={showDerbyPicks && !!derbyPicks.length}
      derbyPicks={derbyPicks}
      isNumberFire={isNumberFire}
      derbyRaceUrl={derbyPath}
      promoText={promoText}
      headerCTA={headerCTA}
      onHeaderClick={onHeaderClick}
      onPromoClick={onPromoClick}
      onFooterButtonClick={onFooterButtonClick}
      baseBet={baseBet}
    />
  );
};

export default HomeHeaderWrapper;
